'use client';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  toast,
} from '@repo/ui';
import type React from 'react';
import type { JSX } from 'react';
import { useState, useTransition } from 'react';

interface IRemovePasskeyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRemove: () => Promise<void>;
}

export function RemovePasskeyDialog({
  open,
  onOpenChange,
  onRemove,
}: IRemovePasskeyDialogProps): JSX.Element {
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  const handleRemove = (): void => {
    setError(null);
    startTransition(async () => {
      try {
        await onRemove();
        toast.success('Passkey removed');
        onOpenChange(false);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to remove passkey';
        setError(message);
        toast.error(message);
      }
    });
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove Passkey</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to remove your passkey? You will no longer be able to sign in
            using biometrics on this device.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error && (
          <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">{error}</div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              handleRemove();
            }}
            disabled={isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isPending ? 'Removing...' : 'Remove'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
